import { Injectable } from '@angular/core';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import {
    GetAccountSummaryForUserRequest,
    GetAccountSummaryForUserService
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import { ClaimService } from '@lsq-frontend/common-ui';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { shareReplay, switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class IdentityService {
    public companyName = '';
    public companyName$ = new BehaviorSubject('');

    public readonly relationshipName$: Observable<string> = this._userService.user$.pipe(
        switchMap((user) => {
            if (user && this._claimService.isOnSync(DashboardClaim.FinancingVendorPortal)) {
                return this._getAccountSummaryForUserService
                    .executeOnce(
                        new GetAccountSummaryForUserRequest({
                            refUserKey: user.userKey
                        })
                    )
                    .then(
                        (response) =>
                            (response.buyerNamesBySellerKey as Record<string, string>)[
                                user.currentCompany.alternateCompanyKey
                            ]
                    );
            } else {
                return of('');
            }
        }),
        shareReplay(1)
    );

    public hasMultipleCompaniesOrANetworkRequest$ = this._userService.user$.pipe(
        switchMap(async (user) => {
            if (!user || !user.companies || user.companies.length === 0) {
                return false;
            } else if (user.companies.length > 1) {
                return true;
            } else {
                return await this._getAccountSummaryForUserService
                    .executeOnce(
                        new GetAccountSummaryForUserRequest({
                            refUserKey: user?.userKey
                        })
                    )
                    .then((response) => response.networkRequestInformation.length > 0)
                    .catch((e) => {
                        /*
                        This is specific for the demo env.
                        There is no java env for this call.
                        For the demo user we are returning false.
                        */
                        if (e.code == '404') return false;
                        else throw e;
                    });
            }
        }),
        shareReplay(1)
    );

    constructor(
        private _userService: UserService,
        private _claimService: ClaimService,
        private _getAccountSummaryForUserService: GetAccountSummaryForUserService
    ) {
        SingletonServiceGuard.register('IdentityService');
        this.companyName$.subscribe((cn) => (this.companyName = cn));
    }

    public isScfBuyer(): boolean {
        return this._claimService.isOnSync(DashboardClaim.FinancingBuyerPortal);
    }

    public isScfBuyerAccount(): boolean {
        return this._claimService.isOnSync(DashboardClaim.FinancingAccountPortal);
    }

    public isScfSeller(): boolean {
        return this._claimService.isOnSync(DashboardClaim.FinancingVendorPortal);
    }
}
